import { Issuers } from '@/constants/issuers';

import { type Issuer, isRwaIssuer } from '@/utils/0x/token-registry.types';

type GeoRestrictionSubject = Readonly<{
  issuer?: string;
  isRwa: boolean;
}>;

type GeoRestrictionRule = Readonly<{
  match: Readonly<{
    issuer?: Issuers;
    isRwa?: true;
  }>;
  countries: readonly string[];
}>;

/**
 * Additive token geo-restriction policy. A token is restricted in the union of
 * countries from every rule matching its issuer and RWA classification.
 */
const GEO_RESTRICTION_RULES = [
  {
    match: { isRwa: true },
    countries: ['US'],
  },
  {
    match: { issuer: Issuers.MAPLE },
    countries: ['US'],
  },
  {
    match: { issuer: Issuers.COINBASE, isRwa: true },
    countries: ['AU', 'SG', 'GB', 'CA'],
  },
  {
    match: { issuer: Issuers.ONDO },
    countries: ['AF', 'BY', 'CA', 'CU', 'KP', 'IR', 'LY', 'MM', 'RU', 'SO', 'SS', 'SD', 'SY', 'UA'],
  },
] as const satisfies readonly GeoRestrictionRule[];

const ANY_RESTRICTED_COUNTRIES: ReadonlySet<string> = new Set(
  GEO_RESTRICTION_RULES.flatMap(({ countries }) => countries),
);

/** True when the geo header was absent, so no country could be resolved. */
const isUnknownCountry = (country: string): boolean => !country || country === 'unknown';

const ruleMatchesSubject = (
  { match }: GeoRestrictionRule,
  subject: GeoRestrictionSubject,
): boolean => {
  if (match.issuer !== undefined && match.issuer !== subject.issuer) return false;
  return match.isRwa === undefined || match.isRwa === subject.isRwa;
};

const getRestrictedCountriesForSubject = (subject: GeoRestrictionSubject): readonly string[] => [
  ...new Set(
    GEO_RESTRICTION_RULES.filter((rule) => ruleMatchesSubject(rule, subject)).flatMap(
      ({ countries }) => countries,
    ),
  ),
];

/** Countries where this issuer's tokens cannot be traded; empty when unrestricted. */
export const getRestrictedCountriesForIssuer = (issuer?: Issuer): readonly string[] =>
  getRestrictedCountriesForSubject({
    issuer: issuer?.name?.toLowerCase(),
    isRwa: isRwaIssuer(issuer),
  });

/**
 * Whether this issuer's tokens are barred from `country`. Fails closed: an
 * unresolved country blocks every restricted issuer.
 */
export const isIssuerRestrictedInCountry = (
  issuer: Issuer | undefined,
  country: string,
): boolean => {
  const restricted = getRestrictedCountriesForIssuer(issuer);
  if (restricted.length === 0) return false;
  return isUnknownCountry(country) || restricted.includes(country);
};

/**
 * Whether `country` bars an otherwise unspecified RWA token. Used by the
 * backwards-compatible geo-check response, where no token issuer is available.
 * Fails closed for an unresolved country.
 */
export const isRwaRestrictedInCountry = (country: string): boolean => {
  const restricted = getRestrictedCountriesForSubject({ isRwa: true });
  return isUnknownCountry(country) || restricted.includes(country);
};

/**
 * Whether any issuer is restricted in `country`, i.e. whether screening a pair
 * against the Token API can change the outcome. Callers use this to skip the
 * round trip entirely for unrestricted regions. Fails closed.
 */
export const isRestrictedCountry = (country: string): boolean =>
  isUnknownCountry(country) || ANY_RESTRICTED_COUNTRIES.has(country);
